import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

export interface GeneratedIdea {
  title: string;
  description: string;
  learning_objectives?: { objective: string; category: string }[];
  deliverables?: { item: string; type: string; description: string }[];
  skills?: { skill: string; level: string }[];
  suggested_timeline?: string;
  complexity?: string;
}

interface IdeasDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ideas: GeneratedIdea[] | null;
  isLoading: boolean;
  error: string | null;
  onUseIdea: (idea: GeneratedIdea) => void;
}

export const IdeasDrawer = ({
  open,
  onOpenChange,
  ideas,
  isLoading,
  error,
  onUseIdea,
}: IdeasDrawerProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent side="right" className="w-full sm:max-w-3xl">
      <SheetHeader className="border-b pb-3">
        <SheetTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Generated Assignment Ideas{ideas ? ` (${ideas.length})` : ''}
        </SheetTitle>
      </SheetHeader>

      <div className="mt-4 space-y-4 overflow-y-auto pr-2">
        {isLoading ? (
          <div className="rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
            Generating assignment ideas, please do not reload the page.
          </div>
        ) : null}
        {error ? (
          <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {error}
          </div>
        ) : null}
        {(ideas ?? []).map((idea, index) => (
          <div key={`${idea.title}-${index}`} className="rounded-2xl border bg-card p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                  {index + 1}
                </div>
                <div>
                  <p className="text-base font-semibold">{idea.title}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    {idea.suggested_timeline ? (
                      <span className="rounded-full border bg-muted/50 px-2 py-1">
                        Timeline: {idea.suggested_timeline}
                      </span>
                    ) : null}
                    {idea.complexity ? (
                      <span className="rounded-full border bg-muted/50 px-2 py-1">
                        {idea.complexity}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
              <Button type="button" size="sm" onClick={() => onUseIdea(idea)}>
                Use this idea
              </Button>
            </div>

            <div className="mt-4 space-y-4 text-sm text-muted-foreground">
              {idea.description ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Description
                  </p>
                  <p className="mt-1 text-sm text-foreground/80">{idea.description}</p>
                </div>
              ) : null}

              {idea.learning_objectives?.length ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Learning Objectives
                  </p>
                  <div className="mt-2 space-y-2">
                    {idea.learning_objectives.map((objective, idx) => (
                      <div key={`${objective.objective}-${idx}`} className="flex items-start gap-2">
                        <span className="mt-1 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                          {objective.category}
                        </span>
                        <span className="text-foreground/80">{objective.objective}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {idea.deliverables?.length ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Suggested Deliverables
                  </p>
                  <div className="mt-2 space-y-2">
                    {idea.deliverables.map((deliverable, idx) => (
                      <div key={`${deliverable.item}-${idx}`} className="rounded-lg border bg-muted/30 p-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-foreground">{deliverable.item}</span>
                          {deliverable.type ? (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                              {deliverable.type}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{deliverable.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {idea.skills?.length ? (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Relevant Skills/Competencies
                  </p>
                  <div className="mt-2 space-y-2">
                    {idea.skills.map((skill, idx) => (
                      <div key={`${skill.skill}-${idx}`} className="flex items-center justify-between rounded-lg border bg-muted/20 px-3 py-2">
                        <span className="text-foreground">{skill.skill}</span>
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-primary">
                          {skill.level}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ))}

        {!isLoading && ideas && ideas.length === 0 ? (
          <div className="rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
            No ideas returned for this course.
          </div>
        ) : null}
      </div>
    </SheetContent>
  </Sheet>
);
